<template>
  <main class="recipes-list-view">
    <section class="recipes-hero">
      <div class="recipes-hero__inner">
        <button type="button" class="recipes-back" @click="returnFromCatalog">{{ catalogReturnLabel() }}</button>
        <h1 class="recipes-hero__title">Le catalogue de mes recettes</h1>


      </div>
    </section>

    <!-- Bouton filtres fixe (mobile uniquement) -->
    <button type="button" class="recipes-filter-fab" @click="filtersOpen = !filtersOpen">
      <span>{{ filtersOpen ? '✕ Fermer' : '⚙ Filtres' }}</span>
    </button>

    <!-- Overlay mobile -->
    <div v-if="filtersOpen" class="recipes-filter-overlay" @click="filtersOpen = false"></div>

    <section class="recipes-section">
      <div class="recipes-section__inner">
        <div class="recipes-layout">
          <aside class="recipes-sidebar" :class="{ 'recipes-sidebar--open': filtersOpen }">
            <form class="recipes-filters" method="get" action="/recettes" @submit.prevent="submitFilters">
              <div class="recipes-filters__actions recipes-filters__actions--top">
                <button type="button" class="recipes-reset" :disabled="!hasActiveFilters" @click="resetFilters">
                  Réinitialiser les filtres
                </button>
              </div>

              <div class="recipes-filters__stack">
                <div class="recipes-field recipes-field--free-search">
                  <label for="free-recipe-search" class="recipes-field__label">Ma recherche libre</label>
                  <div class="recipes-autocomplete">
                    <input
                      id="free-recipe-search"
                      v-model="filters.s"
                      type="search"
                      class="recipes-field__control"
                      placeholder="Ex. poulet pomme de terre"
                      autocomplete="off"
                      @input="handleFreeSearchInput"
                      @focus="handleFreeSearchInput"
                      @keydown.down.prevent="moveAutocomplete(1)"
                      @keydown.up.prevent="moveAutocomplete(-1)"
                      @keydown.enter.prevent="handleFreeSearchEnter"
                      @keydown.esc="closeAutocomplete"
                    />
                    <div v-if="autocompleteOpen && autocompleteSuggestions.length" class="recipes-autocomplete__menu">
                      <button
                        v-for="(suggestion, index) in autocompleteSuggestions"
                        :key="`${suggestion.name}-${index}`"
                        type="button"
                        class="recipes-autocomplete__option"
                        :class="{ 'is-active': index === autocompleteIndex }"
                        @mousedown.prevent="chooseAutocompleteSuggestion(suggestion)"
                      >
                        <span>{{ suggestion.name }}</span>
                        <small v-if="suggestion.corrected">Correction automatique</small>
                      </button>
                    </div>
                  </div>
                  <small>Je peux saisir une recette ou plusieurs mots-clés. MMM me propose et corrige les aliments au fur et à mesure.</small>
                  <button type="submit" class="recipes-free-search__submit">Rechercher</button>
                </div>

                <section class="recipes-filter-group">
                  <div class="recipes-filter-group__header">
                    <h2 class="recipes-filter-group__title">Types de plat ?</h2>
                  </div>
                  <div class="recipes-filter-options">
                    <label v-for="option in dishTypeOptions" :key="option.value" class="recipes-filter-option"
                      :class="{ 'is-active': filters.dish_type.includes(option.value) }">
                      <input type="checkbox" :checked="filters.dish_type.includes(option.value)"
                        @change="toggleMultiFilter('dish_type', option.value)" />
                      <span>{{ option.label }}</span>
                    </label>
                  </div>
                </section>

                <section class="recipes-filter-group">
                  <div class="recipes-filter-group__header">
                    <h2 class="recipes-filter-group__title">De saison ?</h2>
                  </div>
                  <div class="recipes-filter-options">
                    <label v-for="option in seasonOptions" :key="option.value" class="recipes-filter-option"
                      :class="{ 'is-active': filters.season === option.value }">
                      <input type="radio" name="season-filter" :value="option.value"
                        :checked="filters.season === option.value"
                        @change="selectExclusiveFilter('season', option.value)" />
                      <span>{{ option.label }}</span>
                    </label>
                  </div>
                </section>

                <section class="recipes-filter-group">
                  <div class="recipes-filter-group__header">
                    <h2 class="recipes-filter-group__title">Végétarien ?</h2>
                  </div>
                  <div class="recipes-filter-options">
                    <label v-for="option in vegetarianOptions" :key="option.value" class="recipes-filter-option"
                      :class="{ 'is-active': filters.vegetarian.includes(option.value) }">
                      <input type="checkbox" :checked="filters.vegetarian.includes(option.value)"
                        @change="toggleMultiFilter('vegetarian', option.value)" />
                      <span>{{ option.label }}</span>
                    </label>
                  </div>
                </section>

                <section class="recipes-filter-group">
                  <div class="recipes-filter-group__header">
                    <h2 class="recipes-filter-group__title">Sans gluten ?</h2>
                  </div>
                  <div class="recipes-filter-options">
                    <label v-for="option in yesNoOptions" :key="`gluten-${option.value}`" class="recipes-filter-option"
                      :class="{ 'is-active': filters.gluten_free === option.value }">
                      <input type="checkbox" :checked="filters.gluten_free === option.value"
                        @change="toggleExclusiveFilter('gluten_free', option.value)" />
                      <span>{{ option.label }}</span>
                    </label>
                  </div>
                </section>

                <section class="recipes-filter-group">
                  <div class="recipes-filter-group__header">
                    <h2 class="recipes-filter-group__title">Index glycémique ?</h2>
                  </div>
                  <div class="recipes-filter-options">
                    <label v-for="option in glycemicIndexOptions" :key="option.value" class="recipes-filter-option"
                      :class="{ 'is-active': filters.glycemic_index.includes(option.value) }">
                      <input type="checkbox" :checked="filters.glycemic_index.includes(option.value)"
                        @change="toggleMultiFilter('glycemic_index', option.value)" />
                      <span>{{ option.label }}</span>
                    </label>
                  </div>
                </section>

                <section class="recipes-filter-group">
                  <div class="recipes-filter-group__header">
                    <h2 class="recipes-filter-group__title">Bon marché ?</h2>
                  </div>
                  <div class="recipes-filter-options">
                    <label v-for="option in yesNoOptions" :key="`budget-${option.value}`" class="recipes-filter-option"
                      :class="{ 'is-active': filters.budget === option.value }">
                      <input type="checkbox" :checked="filters.budget === option.value"
                        @change="toggleExclusiveFilter('budget', option.value)" />
                      <span>{{ option.label }}</span>
                    </label>
                  </div>
                </section>

                <section class="recipes-filter-group">
                  <div class="recipes-filter-group__header">
                    <h2 class="recipes-filter-group__title">Temps de préparation + cuisson ?</h2>
                  </div>
                  <div class="recipes-filter-options">
                    <label v-for="option in prepTimeOptions" :key="option.value" class="recipes-filter-option"
                      :class="{ 'is-active': isPrepTimeOptionActive(option.value) }">
                      <input type="checkbox" :checked="isPrepTimeOptionActive(option.value)"
                        @change="togglePrepTimeFilter(option.value)" />
                      <span>{{ option.label }}</span>
                    </label>
                  </div>
                </section>

              </div>

            </form>
          </aside>

          <div class="recipes-content">
            <div class="recipes-results-heading">
              <p class="recipes-results-heading__eyebrow">{{ hasRecipeSearchCriteria ? 'Ma sélection' : 'Toutes mes recettes' }}</p>
              <h2>{{ hasRecipeSearchCriteria ? 'Mes idées de recettes' : 'Je découvre toutes les recettes' }}</h2>
            </div>

            <section class="recipes-toolbar" aria-label="Contrôles de listing">
              <div class="recipes-toolbar__summary">
                <p class="recipes-toolbar__count">
                  {{ totalResults }} recette<span v-if="totalResults !== 1">s</span>
                </p>
                <p class="recipes-toolbar__page">Page {{ currentPage }} sur {{ totalPages }}</p>
              </div>

              <div class="recipes-toolbar__controls">
                <label class="recipes-field recipes-field--compact">
                  <span class="recipes-field__label">Tri</span>
                  <select v-model="filters.sort" class="recipes-field__control" @change="onSortChanged">
                    <option v-for="option in sortOptions" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </option>
                  </select>
                </label>

                <label class="recipes-field recipes-field--compact">
                  <span class="recipes-field__label">Affichage</span>
                  <select v-model="filters.view" class="recipes-field__control" @change="onViewChanged">
                    <option v-for="option in viewOptions" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </option>
                  </select>
                </label>

                <label class="recipes-field recipes-field--compact">
                  <span class="recipes-field__label">Par page</span>
                  <select v-model="filters.per_page" class="recipes-field__control" @change="onPerPageChanged">
                    <option v-for="option in perPageOptions" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </option>
                  </select>
                </label>
              </div>
            </section>

            <LoaderView v-if="loading" text="Chargement des recettes..." />

            <div v-else-if="recipes.length === 0" class="recipes-empty">
              <template v-if="searchSuggestion">
                <p class="recipes-empty__eyebrow">Je vérifie ma recherche</p>
                <h2>Voulai-je dire « {{ searchSuggestion }} » ?</h2>
                <p>Je peux corriger ma recherche et relancer les résultats.</p>
                <button type="button" class="recipes-empty__action" @click="applySearchSuggestion">
                  Rechercher « {{ searchSuggestion }} »
                </button>
              </template>
              <template v-else-if="seasonalFallbackAvailable">
                <p class="recipes-empty__eyebrow">Ce n'est pas la saison actuellement</p>
                <h2>Je ne trouve pas de recette de saison avec « {{ filters.s }} »</h2>
                <p>
                  Mes Meilleurs Menus privilégie les produits de saison. Si j'ai déjà cet aliment, notamment surgelé,
                  je peux quand même consulter les recettes qui l'utilisent.
                </p>
                <button type="button" class="recipes-empty__action" @click="showOutOfSeasonRecipes">
                  Voir les {{ seasonalFallbackCount }} recette<span v-if="seasonalFallbackCount > 1">s</span> avec « {{ filters.s }} »
                </button>
              </template>
              <template v-else>
                <h2>Je ne trouve pas encore de recette correspondant à ma recherche</h2>
                <p>Je peux modifier mes critères ou consulter toutes les recettes.</p>
              </template>
            </div>

            <div v-else>
              <div v-if="outOfSeasonNoticeAvailable" class="recipes-season-notice">
                <p class="recipes-empty__eyebrow">Ce n'est pas la saison actuellement</p>
                <h2>Je consulte quand même les recettes avec « {{ filters.s }} »</h2>
                <p>
                  Mes Meilleurs Menus privilégie les produits de saison. J'ai choisi d'afficher toutes les recettes :
                  je peux donc voir celles qui utilisent actuellement au moins un ingrédient hors saison,
                  notamment si je l'ai déjà ou si je l'utilise surgelé.
                </p>
              </div>

              <div class="recipes-grid" :class="{ 'recipes-grid--list': filters.view === 'list' }">
              <article v-for="recipe in recipes" :key="recipe.id" class="recipe-card">
                <router-link class="recipe-card__link" :to="recipeRoute(recipe)">
                  <div class="recipe-card__media">
                    <img v-if="recipe.photo" :src="mediaSrc(recipe.photo)" :alt="recipe.name"
                      class="recipe-card__image" />
                    <div v-else class="recipe-card__image recipe-card__image--fallback">
                      <span>{{ recipe.name }}</span>
                    </div>
                  </div>

                  <div class="recipe-card__content">
                    <div class="recipe-card__quickfacts">
                      <span v-if="recipe.preparation_time">⏱ {{ recipe.preparation_time }} min</span>
                      <span v-if="recipe.glycemic_index">{{ humanizeGlycemicIndex(recipe.glycemic_index) }}</span>
                      <span v-if="recipe.price">{{ recipe.price }}</span>
                    </div>
                    <h2 class="recipe-card__title">{{ recipe.name }}</h2>
                    <div v-if="recipe.mealim_score != null" class="recipe-card__mealim-score">
                      <span>Score MEALIM</span>
                      <strong>{{ recipe.mealim_score }}/100</strong>
                    </div>
                    <p v-if="recipe._outOfSeason" class="recipe-card__season-note">
                      🌱 Je garde en tête qu’au moins un ingrédient est hors saison actuellement.
                    </p>

                    <!-- Badges info — visibles uniquement en mode liste -->
                    <div class="recipe-card__badges">
                      <span class="recipe-badge" v-if="recipe.difficulty">
                        <font-awesome-icon :icon="['fas', 'signal']" /> {{ recipe.difficulty }}
                      </span>
                      <span class="recipe-badge" v-if="recipe.preparation_time">
                        <font-awesome-icon :icon="['fas', 'clock']" /> {{ recipe.preparation_time }} min
                      </span>
                      <span class="recipe-badge" v-if="recipe.price">
                        <font-awesome-icon :icon="['fas', 'wallet']" /> {{ recipe.price }}
                      </span>
                      <span class="recipe-badge" v-if="recipe.glycemic_index">
                        <font-awesome-icon :icon="['fas', 'chart-line']" /> {{ humanizeGlycemicIndex(recipe.glycemic_index) }}
                      </span>
                      <span class="recipe-badge" v-if="recipe.macro_nutriments">
                        <font-awesome-icon :icon="['fas', 'layer-group']" /> {{ humanizeMacroNutriments(recipe.macro_nutriments) }}
                      </span>
                      <span class="recipe-badge" :class="recipe.is_vegan ? 'recipe-badge--yes' : 'recipe-badge--no'">
                        <font-awesome-icon :icon="['fas', 'seedling']" />
                        <font-awesome-icon :icon="recipe.is_vegan ? ['fas', 'check'] : ['fas', 'xmark']" />
                      </span>
                      <span class="recipe-badge" :class="recipe.is_gluten_free ? 'recipe-badge--yes' : 'recipe-badge--no'">
                        <font-awesome-icon :icon="['fas', 'ban']" />
                        <font-awesome-icon :icon="recipe.is_gluten_free ? ['fas', 'check'] : ['fas', 'xmark']" />
                      </span>
                    </div>

                    <span class="recipe-card__cta">Voir la recette</span>
                  </div>
                </router-link>
              </article>
              </div>
            </div>

            <nav v-if="!loading && totalPages > 1" class="recipes-pagination" aria-label="Pagination des recettes">
              <button type="button" class="recipes-pagination__button" :disabled="currentPage <= 1"
                @click="goToPage(currentPage - 1)">
                Précédent
              </button>

              <button v-for="page in visiblePages" :key="page" type="button" class="recipes-pagination__button"
                :class="{ 'recipes-pagination__button--active': page === currentPage }" @click="goToPage(page)">
                {{ page }}
              </button>

              <button type="button" class="recipes-pagination__button" :disabled="currentPage >= totalPages"
                @click="goToPage(currentPage + 1)">
                Suivant
              </button>
            </nav>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { api } from "@/api";
import LoaderView from "@/components/LoaderView.vue";

const DEFAULT_FILTERS = {
  s: "",
  dish_type: [],
  vegetarian: [],
  gluten_free: "",
  glycemic_index: [],
  budget: "",
  prep_time: [],
  season: "actuelle",
  sort: "newest",
  view: "grid",
  per_page: "20",
  page: 1,
};

const MULTI_FILTER_KEYS = ["dish_type", "vegetarian", "glycemic_index", "prep_time"];

export default {
  name: "RecipesListView",
  components: {
    LoaderView,
  },
  data() {
    return {
      loading: true,
      filtersOpen: false,
      recipeFinder: {
        vegetable: "",
        profile: "",
        time: "",
      },
      finderProfileOptions: [
        { value: "", label: "Peu importe", icon: "🍽️" },
        { value: "poisson", label: "Poisson", icon: "🐟" },
        { value: "viande", label: "Viande", icon: "🍗" },
        { value: "vegetarien", label: "Végétarien", icon: "🌱" },
      ],
      finderTimeOptions: [
        { value: "", label: "Peu importe" },
        { value: "lt20", label: "Moins de 20 min" },
        { value: "lt30", label: "Moins de 30 min" },
        { value: "lt40", label: "Moins de 40 min" },
      ],
      recipes: [],
      seasonalFallbackAvailable: false,
      seasonalFallbackCount: 0,
      outOfSeasonNoticeAvailable: false,
      searchSuggestion: "",
      searchMode: "",
      autocompleteSuggestions: [],
      autocompleteOpen: false,
      autocompleteIndex: -1,
      autocompleteTimer: null,
      autocompleteRequestId: 0,
      vegetableSuggestions: [],
      vegetableAutocompleteOpen: false,
      vegetableAutocompleteIndex: -1,
      vegetableAutocompleteTimer: null,
      vegetableRequestId: 0,
      vegetableCatalog: [],
      totalResults: 0,
      currentPage: 1,
      totalPages: 1,
      filters: this.getDefaultFilters(),
      dishTypeOptions: [
        { value: "entree", label: "Entrée" },
        { value: "plat", label: "Plat" },
        { value: "dessert", label: "Dessert" },
      ],
      vegetarianOptions: [
        { value: "vegetarien", label: "Végétarien" },
        { value: "viande", label: "Contient de la viande" },
        { value: "poisson", label: "Contient du poisson" },
      ],
      yesNoOptions: [
        { value: "oui", label: "Oui" },
        { value: "non", label: "Non" },
      ],
      glycemicIndexOptions: [
        { value: "faible", label: "Faible" },
        { value: "moyen", label: "Moyen" },
        { value: "haut", label: "Haut" },
      ],
      prepTimeOptions: [
        { value: "any", label: "Peu importe" },
        { value: "lt20", label: "< 20 min" },
        { value: "lt30", label: "< 30 min" },
        { value: "lt40", label: "< 40 min" },
        { value: "gte60", label: "60 min ou plus" },
      ],
      seasonOptions: [
        { value: "actuelle", label: "Recettes de saison" },
        { value: "all", label: "Toutes les recettes" },
      ],
      sortOptions: [
        { value: "newest", label: "Plus récentes" },
        { value: "name_asc", label: "Nom A -> Z" },
        { value: "name_desc", label: "Nom Z -> A" },
        { value: "prep_time_asc", label: "Préparation croissante" },
        { value: "prep_time_desc", label: "Préparation décroissante" },
      ],
      viewOptions: [
        { value: "grid", label: "Grille" },
        { value: "list", label: "Liste" },
      ],
      perPageOptions: [
        { value: "20", label: "20" },
        { value: "40", label: "40" },
        { value: "80", label: "80" },
      ],
    };
  },
  computed: {
    hasRecipeSearchCriteria() {
      return Boolean(
        this.filters.s ||
        this.filters.dish_type.length ||
        this.filters.vegetarian.length ||
        this.filters.gluten_free ||
        this.filters.glycemic_index.length ||
        this.filters.budget ||
        this.filters.prep_time.length
      );
    },
    hasActiveFilters() {
      const { query } = this.$route;

      return ["s", "dish_type", "vegetarian", "gluten_free", "glycemic_index", "budget", "prep_time", "season"]
        .some((key) => {
          const value = query[key];
          if (Array.isArray(value)) {
            return value.some((item) => String(item || "").trim());
          }

          return !!String(value || "").trim();
        });
    },
    visiblePages() {
      const pages = [];
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(this.totalPages, this.currentPage + 2);

      for (let page = start; page <= end; page += 1) {
        pages.push(page);
      }

      return pages;
    },
  },
  created() {
    const validSeasons = ["actuelle", "all"];
    if (!validSeasons.includes(this.$route.query.season)) {
      this.$router.replace({
        name: "recipes",
        query: { ...this.$route.query, season: "actuelle" },
      }).catch(() => {});
    }
  },
  watch: {
    "$route.query": {
      immediate: true,
      handler() {
        this.applyRouteQueryToFilters();
        this.updateHead();
        this.loadRecipes();
      },
    },
  },
  methods: {
    ensureVegetableCatalog() {
      if (this.vegetableCatalog.length) {
        return Promise.resolve(this.vegetableCatalog);
      }

      return Promise.all([
        api.get("/aliments/", { params: { q: "légume" } }),
        api.get("/aliments/", { params: { q: "legume" } }),
      ])
        .then((responses) => {
          const seen = new Set();
          this.vegetableCatalog = responses
            .flatMap((response) => Array.isArray(response.data) ? response.data : [])
            .filter((candidate) => {
              const name = candidate && candidate.name ? String(candidate.name).trim() : "";
              const key = this.normalizeForComparison(name);
              if (!key || seen.has(key)) return false;
              seen.add(key);
              return true;
            });
          return this.vegetableCatalog;
        })
        .catch(() => []);
    },
    handleVegetableInput() {
      window.clearTimeout(this.vegetableAutocompleteTimer);
      const value = String(this.recipeFinder.vegetable || "").trim();

      if (value.length < 2) {
        this.closeVegetableAutocomplete();
        return;
      }

      this.vegetableAutocompleteTimer = window.setTimeout(() => {
        this.loadVegetableSuggestions(value);
      }, 160);
    },
    loadVegetableSuggestions(value) {
      const requestId = ++this.vegetableRequestId;
      this.ensureVegetableCatalog().then((catalog) => {
        if (requestId !== this.vegetableRequestId) return;

        const ranked = this.rankAutocompleteCandidates(value, catalog, true)
          .filter((candidate) => {
            const normalizedValue = this.normalizeForComparison(value);
            const normalizedName = this.normalizeForComparison(candidate.name);
            return (
              normalizedName.startsWith(normalizedValue) ||
              normalizedName.includes(normalizedValue) ||
              candidate.distance <= (normalizedValue.length <= 5 ? 1 : 2)
            );
          })
          .slice(0, 6);

        const best = ranked[0];
        const maxDistance = this.normalizeForComparison(value).length <= 5 ? 1 : 2;
        if (
          best &&
          best.distance > 0 &&
          best.distance <= maxDistance &&
          !this.normalizeForComparison(best.name).includes(this.normalizeForComparison(value))
        ) {
          this.recipeFinder.vegetable = best.name;
          ranked[0] = { ...best, corrected: true };
        }

        this.vegetableSuggestions = ranked;
        this.vegetableAutocompleteIndex = ranked.length ? 0 : -1;
        this.vegetableAutocompleteOpen = ranked.length > 0;
      });
    },
    moveVegetableAutocomplete(direction) {
      if (!this.vegetableAutocompleteOpen || !this.vegetableSuggestions.length) return;
      const length = this.vegetableSuggestions.length;
      this.vegetableAutocompleteIndex =
        (this.vegetableAutocompleteIndex + direction + length) % length;
    },
    chooseVegetableSuggestion(suggestion) {
      if (!suggestion || !suggestion.name) return;
      this.recipeFinder.vegetable = suggestion.name;
      this.closeVegetableAutocomplete();
    },
    handleVegetableEnter() {
      if (
        this.vegetableAutocompleteOpen &&
        this.vegetableAutocompleteIndex >= 0 &&
        this.vegetableSuggestions[this.vegetableAutocompleteIndex]
      ) {
        this.chooseVegetableSuggestion(
          this.vegetableSuggestions[this.vegetableAutocompleteIndex],
        );
        return;
      }
      this.applyRecipeFinder();
    },
    closeVegetableAutocomplete() {
      this.vegetableAutocompleteOpen = false;
      this.vegetableSuggestions = [];
      this.vegetableAutocompleteIndex = -1;
    },
    getActiveSearchFragment() {
      const value = String(this.filters.s || "");
      const match = value.match(/([^,;+]+)$/);
      if (!match) return "";
      const chunk = match[1].trim();
      const words = chunk.split(/\s+/).filter(Boolean);
      return words.length ? words[words.length - 1] : "";
    },
    replaceActiveSearchFragment(replacement) {
      const value = String(this.filters.s || "");
      const fragment = this.getActiveSearchFragment();
      if (!fragment) return;

      const index = value.toLowerCase().lastIndexOf(fragment.toLowerCase());
      if (index < 0) return;

      this.filters.s = `${value.slice(0, index)}${replacement}${value.slice(index + fragment.length)}`
        .replace(/\s+/g, " ")
        .trimStart();
    },
    handleFreeSearchInput() {
      window.clearTimeout(this.autocompleteTimer);
      const fragment = this.getActiveSearchFragment();
      const ignoredFragments = new Set(["de", "du", "des", "et", "avec", "au", "aux"]);

      if (fragment.length < 2 || ignoredFragments.has(fragment.toLowerCase())) {
        this.closeAutocomplete();
        return;
      }

      this.autocompleteTimer = window.setTimeout(() => {
        this.loadAutocompleteSuggestions(fragment);
      }, 180);
    },
    loadAutocompleteSuggestions(fragment) {
      const requestId = ++this.autocompleteRequestId;
      const clean = fragment.trim();
      if (clean.length < 2) return;

      api.get("/aliments/", { params: { q: clean } })
        .then((response) => {
          if (requestId !== this.autocompleteRequestId) return null;
          const direct = this.rankAutocompleteCandidates(clean, response.data || [], false);
          if (direct.length) {
            this.autocompleteSuggestions = direct.slice(0, 6);
            this.autocompleteIndex = 0;
            this.autocompleteOpen = true;
            return null;
          }

          const prefix = this.normalizeForComparison(clean).slice(0, clean.length >= 5 ? 3 : 2);
          if (prefix.length < 2) return null;

          return api.get("/aliments/", { params: { q: prefix } }).then((fallbackResponse) => {
            if (requestId !== this.autocompleteRequestId) return;
            const fuzzy = this.rankAutocompleteCandidates(clean, fallbackResponse.data || [], true);
            this.autocompleteSuggestions = fuzzy.slice(0, 6);
            this.autocompleteIndex = fuzzy.length ? 0 : -1;
            this.autocompleteOpen = fuzzy.length > 0;

            const best = fuzzy[0];
            const maxDistance = clean.length <= 5 ? 1 : 2;
            if (best && best.distance <= maxDistance && best.name) {
              this.replaceActiveSearchFragment(best.name);
              this.autocompleteSuggestions = [{ ...best, corrected: true }, ...fuzzy.slice(1, 5)];
            }
          });
        })
        .catch(() => {
          if (requestId === this.autocompleteRequestId) this.closeAutocomplete();
        });
    },
    rankAutocompleteCandidates(fragment, candidates, fuzzy) {
      const normalizedFragment = this.normalizeForComparison(fragment);
      const seen = new Set();

      return candidates
        .map((candidate) => {
          const name = candidate && candidate.name ? String(candidate.name).trim() : "";
          const normalizedName = this.normalizeForComparison(name);
          if (!name || !normalizedName || seen.has(normalizedName)) return null;
          seen.add(normalizedName);

          const starts = normalizedName.startsWith(normalizedFragment);
          const contains = normalizedName.includes(normalizedFragment);
          const firstWord = name.split(/[,(]/)[0].trim();
          const distance = this.levenshteinDistance(fragment, firstWord);

          return {
            name: firstWord || name,
            distance,
            corrected: fuzzy && !starts && !contains,
            score: starts ? 0 : contains ? 1 : 2 + distance,
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.score - b.score || a.distance - b.distance || a.name.length - b.name.length);
    },
    moveAutocomplete(direction) {
      if (!this.autocompleteOpen || !this.autocompleteSuggestions.length) return;
      const length = this.autocompleteSuggestions.length;
      this.autocompleteIndex = (this.autocompleteIndex + direction + length) % length;
    },
    chooseAutocompleteSuggestion(suggestion) {
      if (!suggestion || !suggestion.name) return;
      this.replaceActiveSearchFragment(suggestion.name);
      this.closeAutocomplete();
    },
    handleFreeSearchEnter() {
      if (
        this.autocompleteOpen &&
        this.autocompleteIndex >= 0 &&
        this.autocompleteSuggestions[this.autocompleteIndex]
      ) {
        this.chooseAutocompleteSuggestion(this.autocompleteSuggestions[this.autocompleteIndex]);
        return;
      }
      this.submitFilters();
    },
    closeAutocomplete() {
      this.autocompleteOpen = false;
      this.autocompleteSuggestions = [];
      this.autocompleteIndex = -1;
    },
    applyRecipeFinder() {
      this.closeVegetableAutocomplete();
      this.searchMode = "finder";
      this.filters.s = this.recipeFinder.vegetable.trim();
      this.filters.vegetarian = this.recipeFinder.profile ? [this.recipeFinder.profile] : [];
      this.filters.prep_time = this.recipeFinder.time ? [this.recipeFinder.time] : [];
      this.filters.season = "actuelle";
      this.filters.page = 1;
      this.pushRouteQuery();

      this.$nextTick(() => {
        const results = document.querySelector(".recipes-content");
        if (results) {
          results.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    showAllRecipes() {
      this.searchMode = "";
      this.recipeFinder = { vegetable: "", profile: "", time: "" };
      this.filters = this.getDefaultFilters();
      this.filters.season = "all";
      this.filters.page = 1;
      this.pushRouteQuery();

      this.$nextTick(() => {
        const results = document.querySelector(".recipes-content");
        if (results) {
          results.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    slugifyRecipeName(value) {
      return String(value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    },
    recipeRoute(recipe) {
      return {
        name: "recipe",
        params: {
          id: recipe.id,
          slug: this.slugifyRecipeName(recipe.name) || String(recipe.id),
        },
      };
    },
    catalogReturnLabel() {
      try {
        return window.sessionStorage.getItem("mmm-shared-page-context") === "personal"
          ? "Retour à mon espace"
          : "Accueil";
      } catch (e) {
        return "Accueil";
      }
    },
    returnFromCatalog() {
      let personalContext = false;
      try {
        personalContext = window.sessionStorage.getItem("mmm-shared-page-context") === "personal";
        if (personalContext) {
          const target = window.sessionStorage.getItem("mmm-recipes-return-route");
          window.sessionStorage.removeItem("mmm-recipes-return-route");
          if (target && target.startsWith("/") && !target.startsWith("//") && target !== this.$route.fullPath) {
            this.$router.push(target).catch(() => {});
            return;
          }
        }
      } catch (e) {
        personalContext = false;
      }
      this.$router.push(personalContext ? { name: "my-menus" } : { name: "accueil" }).catch(() => {});
    },
    humanizeGlycemicIndex(g) {
      if (g === "low") return "IG faible";
      if (g === "medium") return "IG moyen";
      if (g === "high") return "IG élevé";
      return g || "";
    },
    humanizeMacroNutriments(value) {
      if (!value) return "";
      const map = { P: "Protéines", L: "Lipides", G: "Glucides" };
      return value.split("-").map(l => map[l] || l).join(" - ");
    },
    getDefaultFilters() {
      return {
        ...DEFAULT_FILTERS,
        dish_type: [],
        vegetarian: [],
        glycemic_index: [],
        prep_time: [],
      };
    },
    loadRecipes() {
      this.loading = true;
      this.seasonalFallbackAvailable = false;
      this.seasonalFallbackCount = 0;
      this.outOfSeasonNoticeAvailable = false;
      this.searchSuggestion = "";

      const params = this.buildApiQueryFromFilters();

      if (this.searchMode === "free" && this.filters.s) {
        this.loadSmartFreeSearch(params);
        return;
      }

      api.get("/recipes/", { params })
        .then((response) => {
          const payload = response.data || {};
          this.recipes = Array.isArray(payload.results) ? payload.results : [];
          this.totalResults = Number(payload.total_results) || 0;
          this.currentPage = Number(payload.current_page) || 1;
          this.totalPages = Number(payload.total_pages) || 1;

          if (this.recipes.length === 0 && this.filters.s && this.filters.season === "actuelle") {
            return api.get("/recipes/", {
              params: { ...params, season: "all", page: 1 },
            }).then((fallbackResponse) => {
              const fallbackPayload = fallbackResponse.data || {};
              const fallbackCount = Number(fallbackPayload.total_results) || 0;
              this.seasonalFallbackAvailable = fallbackCount > 0;
              this.seasonalFallbackCount = fallbackCount;
            });
          }

          if (this.recipes.length > 0 && this.filters.s && this.filters.season === "all") {
            return api.get("/recipes/", {
              params: { ...params, season: "non", page: 1 },
            }).then((seasonResponse) => {
              const seasonPayload = seasonResponse.data || {};
              this.outOfSeasonNoticeAvailable = (Number(seasonPayload.total_results) || 0) > 0;
            });
          }

          return null;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    parseSmartSearchTerms(value) {
      const words = String(value || "")
        .trim()
        .replace(/[,+;]/g, " ")
        .split(/\s+/)
        .filter(Boolean);
      const ignored = new Set(["et", "avec", "plus", "de", "du", "des", "d'", "d’", "a", "à", "au", "aux"]);
      const connectors = new Set(["de", "du", "des", "d'", "d’"]);
      const terms = [];

      for (let index = 0; index < words.length; index += 1) {
        let term = words[index];
        const connector = words[index + 1] ? words[index + 1].toLowerCase() : "";

        if (connector && connectors.has(connector) && words[index + 2]) {
          term = `${term} ${words[index + 1]} ${words[index + 2]}`;
          index += 2;
        }

        if (!ignored.has(term.toLowerCase()) && term.length > 1) {
          terms.push(term);
        }
      }

      return [...new Set(terms)];
    },
    buildSmartSearchBaseParams(params, season) {
      const next = { ...params, season, per_page: 80, page: 1 };
      delete next.s;
      return next;
    },
    fetchAllRecipePages(params) {
      return api.get("/recipes/", { params: { ...params, page: 1 } })
        .then((response) => {
          const payload = response.data || {};
          const firstResults = Array.isArray(payload.results) ? payload.results : [];
          const pages = Number(payload.total_pages) || 1;

          if (pages <= 1) {
            return firstResults;
          }

          const requests = [];
          for (let page = 2; page <= pages; page += 1) {
            requests.push(api.get("/recipes/", { params: { ...params, page } }));
          }

          return Promise.all(requests).then((responses) => [
            ...firstResults,
            ...responses.flatMap((item) => (
              item.data && Array.isArray(item.data.results) ? item.data.results : []
            )),
          ]);
        });
    },
    fetchSmartTermResults(terms, params) {
      return Promise.all(
        terms.map((term) => this.fetchAllRecipePages({ ...params, s: term })),
      );
    },
    intersectRecipeResults(resultSets) {
      if (!resultSets.length) return [];
      const allowedIds = resultSets.slice(1).map((set) => new Set(set.map((recipe) => recipe.id)));
      return resultSets[0].filter((recipe) => allowedIds.every((ids) => ids.has(recipe.id)));
    },
    loadSmartFreeSearch(params) {
      const terms = this.parseSmartSearchTerms(this.filters.s);

      if (!terms.length) {
        this.recipes = [];
        this.totalResults = 0;
        this.currentPage = 1;
        this.totalPages = 1;
        this.loading = false;
        return;
      }

      // La recherche libre part toujours de toutes les recettes correspondantes.
      // La saisonnalité est ensuite appliquée à partir de la même règle métier
      // que MMM, afin d'éviter qu'un changement du filtre fasse disparaître
      // l'avertissement "hors saison".
      const allParams = this.buildSmartSearchBaseParams(params, "all");
      const nonSeasonParams = this.buildSmartSearchBaseParams(params, "non");

      Promise.all([
        this.fetchSmartTermResults(terms, allParams),
        this.fetchSmartTermResults(terms, nonSeasonParams),
      ])
        .then(([allTermResults, nonSeasonTermResults]) => {
          const allMatches = this.intersectRecipeResults(allTermResults);
          const outOfSeasonIds = new Set(
            this.intersectRecipeResults(nonSeasonTermResults).map((recipe) => recipe.id),
          );
          this.outOfSeasonNoticeAvailable =
            this.filters.season === "all" && outOfSeasonIds.size > 0;

          if (!allMatches.length) {
            const missingTermIndex = allTermResults.findIndex((results) => results.length === 0);
            if (missingTermIndex >= 0) {
              return this.findSearchSuggestion(terms[missingTermIndex]).then((suggestion) => {
                this.searchSuggestion = suggestion
                  ? terms.map((term, index) => index === missingTermIndex ? suggestion : term).join(" ")
                  : "";
                return { matches: [], allMatches: [], outOfSeasonIds };
              });
            }
          }

          const matches = this.filters.season === "actuelle"
            ? allMatches.filter((recipe) => !outOfSeasonIds.has(recipe.id))
            : allMatches;

          if (
            this.filters.season === "actuelle" &&
            matches.length === 0 &&
            allMatches.length > 0
          ) {
            this.seasonalFallbackAvailable = true;
            this.seasonalFallbackCount = allMatches.length;
          }

          return { matches, allMatches, outOfSeasonIds };
        })
        .then(({ matches, outOfSeasonIds }) => {
          const marked = matches.map((recipe) => ({
            ...recipe,
            _outOfSeason: outOfSeasonIds.has(recipe.id),
          }));
          const perPage = Number(this.filters.per_page) || 20;
          const totalPages = Math.max(1, Math.ceil(marked.length / perPage));
          const currentPage = Math.min(this.filters.page, totalPages);
          const startIndex = (currentPage - 1) * perPage;

          this.recipes = marked.slice(startIndex, startIndex + perPage);
          this.totalResults = marked.length;
          this.currentPage = currentPage;
          this.totalPages = totalPages;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    normalizeForComparison(value) {
      return String(value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]/g, "");
    },
    levenshteinDistance(a, b) {
      const left = this.normalizeForComparison(a);
      const right = this.normalizeForComparison(b);
      const rows = Array.from({ length: left.length + 1 }, () => []);
      for (let i = 0; i <= left.length; i += 1) rows[i][0] = i;
      for (let j = 0; j <= right.length; j += 1) rows[0][j] = j;

      for (let i = 1; i <= left.length; i += 1) {
        for (let j = 1; j <= right.length; j += 1) {
          const cost = left[i - 1] === right[j - 1] ? 0 : 1;
          rows[i][j] = Math.min(
            rows[i - 1][j] + 1,
            rows[i][j - 1] + 1,
            rows[i - 1][j - 1] + cost,
          );
        }
      }
      return rows[left.length][right.length];
    },
    findSearchSuggestion(term) {
      const compact = this.normalizeForComparison(term);
      if (compact.length < 3) return Promise.resolve("");

      return api.get("/aliments/", { params: { q: compact.slice(0, 3) } })
        .then((response) => {
          const candidates = Array.isArray(response.data) ? response.data : [];
          let best = null;

          candidates.forEach((candidate) => {
            const name = candidate && candidate.name ? candidate.name : "";
            const distance = this.levenshteinDistance(term, name);
            if (!best || distance < best.distance) {
              best = { name, distance };
            }
          });

          const maxDistance = compact.length <= 5 ? 1 : 2;
          return best && best.distance <= maxDistance ? best.name : "";
        })
        .catch(() => "");
    },
    applySearchSuggestion() {
      if (!this.searchSuggestion) return;
      this.filters.s = this.searchSuggestion;
      this.filters.page = 1;
      this.searchMode = "free";
      this.pushRouteQuery();
    },
    showOutOfSeasonRecipes() {
      this.filters.season = "all";
      this.filters.page = 1;
      this.pushRouteQuery();
    },
    applyRouteQueryToFilters() {
      const routeQuery = this.$route.query;
      this.searchMode = this.getSingleQueryValue(routeQuery.mode) === "free" ? "free" : "";

      this.filters = {
        s: this.normalizeSearchValue(routeQuery.s),
        dish_type: this.normalizeMultiChoiceValue(
          routeQuery.dish_type,
          this.dishTypeOptions.map((option) => option.value),
        ),
        vegetarian: this.normalizeMultiChoiceValue(
          routeQuery.vegetarian,
          this.vegetarianOptions.map((option) => option.value),
        ),
        gluten_free: this.normalizeChoiceValue(
          routeQuery.gluten_free,
          this.yesNoOptions.map((option) => option.value),
        ),
        glycemic_index: this.normalizeMultiChoiceValue(
          routeQuery.glycemic_index,
          this.glycemicIndexOptions.map((option) => option.value),
        ),
        budget: this.normalizeChoiceValue(
          routeQuery.budget,
          this.yesNoOptions.map((option) => option.value),
        ),
        prep_time: this.normalizeMultiChoiceValue(
          routeQuery.prep_time,
          this.getPrepTimeFilterValues(),
        ),
        season: this.normalizeChoiceValue(
          routeQuery.season,
          this.seasonOptions.map((option) => option.value),
          "actuelle",
        ),
        sort: this.normalizeChoiceValue(
          routeQuery.sort,
          this.sortOptions.map((option) => option.value),
          DEFAULT_FILTERS.sort,
        ),
        view: this.normalizeChoiceValue(
          routeQuery.view,
          this.viewOptions.map((option) => option.value),
          DEFAULT_FILTERS.view,
        ),
        per_page: this.normalizeChoiceValue(
          routeQuery.per_page,
          this.perPageOptions.map((option) => option.value),
          DEFAULT_FILTERS.per_page,
        ),
        page: this.normalizePageValue(routeQuery.page),
      };
    },
    getSingleQueryValue(value) {
      if (Array.isArray(value)) {
        return value[0] || "";
      }

      return typeof value === "string" ? value : "";
    },
    getQueryValues(value) {
      const rawValues = Array.isArray(value) ? value : [value];

      return rawValues
        .flatMap((item) => String(item || "").split(","))
        .map((item) => item.trim())
        .filter(Boolean);
    },
    normalizeSearchValue(value) {
      return this.getSingleQueryValue(value).trim();
    },
    normalizeChoiceValue(value, allowedValues, fallback = "") {
      const normalizedValue = this.getSingleQueryValue(value);

      if (allowedValues.includes(normalizedValue)) {
        return normalizedValue;
      }

      return fallback;
    },
    normalizeMultiChoiceValue(value, allowedValues) {
      return [...new Set(this.getQueryValues(value).filter((item) => allowedValues.includes(item)))];
    },
    getPrepTimeFilterValues() {
      return this.prepTimeOptions
        .filter((option) => option.value !== "any")
        .map((option) => option.value);
    },
    normalizePageValue(value) {
      const page = Number.parseInt(this.getSingleQueryValue(value), 10);
      if (Number.isInteger(page) && page > 0) {
        return page;
      }

      return 1;
    },
    buildJoinedQueryValue(values) {
      return values.join(",");
    },
    buildApiQueryFromFilters() {
      const query = {};

      if (this.filters.s) {
        query.s = this.filters.s;
      }
      if (this.filters.dish_type.length) {
        query.dish_type = this.buildJoinedQueryValue(this.filters.dish_type);
      }
      if (this.filters.vegetarian.length) {
        query.vegetarian = this.buildJoinedQueryValue(this.filters.vegetarian);
      }
      if (this.filters.gluten_free) {
        query.gluten_free = this.filters.gluten_free;
      }
      if (this.filters.glycemic_index.length) {
        query.glycemic_index = this.buildJoinedQueryValue(this.filters.glycemic_index);
      }
      if (this.filters.budget) {
        query.budget = this.filters.budget;
      }
      if (this.filters.prep_time.length) {
        query.prep_time = this.buildJoinedQueryValue(this.filters.prep_time);
      }
      if (this.filters.season) {
        query.season = this.filters.season;
      }

      query.sort = this.filters.sort;
      query.per_page = this.filters.per_page;
      query.page = this.filters.page;

      return query;
    },
    buildRouteQueryFromFilters() {
      const query = {};

      if (this.filters.s) {
        query.s = this.filters.s;
      }
      if (this.searchMode === "free") {
        query.mode = "free";
      }
      if (this.filters.dish_type.length) {
        query.dish_type = this.buildJoinedQueryValue(this.filters.dish_type);
      }
      if (this.filters.vegetarian.length) {
        query.vegetarian = this.buildJoinedQueryValue(this.filters.vegetarian);
      }
      if (this.filters.gluten_free) {
        query.gluten_free = this.filters.gluten_free;
      }
      if (this.filters.glycemic_index.length) {
        query.glycemic_index = this.buildJoinedQueryValue(this.filters.glycemic_index);
      }
      if (this.filters.budget) {
        query.budget = this.filters.budget;
      }
      if (this.filters.prep_time.length) {
        query.prep_time = this.buildJoinedQueryValue(this.filters.prep_time);
      }
      if (this.filters.season) {
        query.season = this.filters.season;
      }
      if (this.filters.sort !== DEFAULT_FILTERS.sort) {
        query.sort = this.filters.sort;
      }
      if (this.filters.view !== DEFAULT_FILTERS.view) {
        query.view = this.filters.view;
      }
      if (this.filters.per_page !== DEFAULT_FILTERS.per_page) {
        query.per_page = this.filters.per_page;
      }
      if (this.filters.page > 1) {
        query.page = String(this.filters.page);
      }

      return query;
    },
    buildNormalizedRouteQuery(routeQuery) {
      const normalizedFilters = {
        s: this.normalizeSearchValue(routeQuery.s),
        dish_type: this.normalizeMultiChoiceValue(
          routeQuery.dish_type,
          this.dishTypeOptions.map((option) => option.value),
        ),
        vegetarian: this.normalizeMultiChoiceValue(
          routeQuery.vegetarian,
          this.vegetarianOptions.map((option) => option.value),
        ),
        gluten_free: this.normalizeChoiceValue(
          routeQuery.gluten_free,
          this.yesNoOptions.map((option) => option.value),
        ),
        glycemic_index: this.normalizeMultiChoiceValue(
          routeQuery.glycemic_index,
          this.glycemicIndexOptions.map((option) => option.value),
        ),
        budget: this.normalizeChoiceValue(
          routeQuery.budget,
          this.yesNoOptions.map((option) => option.value),
        ),
        prep_time: this.normalizeMultiChoiceValue(
          routeQuery.prep_time,
          this.getPrepTimeFilterValues(),
        ),
        season: this.normalizeChoiceValue(
          routeQuery.season,
          this.seasonOptions.map((option) => option.value),
          "actuelle",
        ),
        sort: this.normalizeChoiceValue(
          routeQuery.sort,
          this.sortOptions.map((option) => option.value),
          DEFAULT_FILTERS.sort,
        ),
        view: this.normalizeChoiceValue(
          routeQuery.view,
          this.viewOptions.map((option) => option.value),
          DEFAULT_FILTERS.view,
        ),
        per_page: this.normalizeChoiceValue(
          routeQuery.per_page,
          this.perPageOptions.map((option) => option.value),
          DEFAULT_FILTERS.per_page,
        ),
        page: this.normalizePageValue(routeQuery.page),
      };

      const currentFilters = this.filters;
      const currentSearchMode = this.searchMode;
      this.filters = normalizedFilters;
      this.searchMode = this.getSingleQueryValue(routeQuery.mode) === "free" ? "free" : "";
      const normalizedQuery = this.buildRouteQueryFromFilters();
      this.filters = currentFilters;
      this.searchMode = currentSearchMode;

      return normalizedQuery;
    },
    submitFilters() {
      this.closeAutocomplete();
      this.filters.s = this.filters.s.trim();
      this.searchMode = this.filters.s ? "free" : "";
      if (this.searchMode === "free") {
        this.filters.season = "all";
      }
      this.filters.page = 1;
      this.pushRouteQuery();
    },
    applyImmediateFilters() {
      this.filters.page = 1;
      this.replaceRouteQuery();
    },
    isPrepTimeOptionActive(optionValue) {
      if (optionValue === "any") {
        return this.filters.prep_time.length === 0;
      }

      return this.filters.prep_time.includes(optionValue);
    },
    togglePrepTimeFilter(optionValue) {
      if (optionValue === "any") {
        this.clearFilterGroup("prep_time");
        return;
      }

      this.toggleMultiFilter("prep_time", optionValue);
    },
    toggleMultiFilter(groupName, optionValue) {
      const nextValues = this.filters[groupName].includes(optionValue)
        ? this.filters[groupName].filter((value) => value !== optionValue)
        : [...this.filters[groupName], optionValue];

      this.filters = {
        ...this.filters,
        [groupName]: nextValues,
      };
      this.applyImmediateFilters();
    },
    toggleExclusiveFilter(groupName, optionValue) {
      this.filters = {
        ...this.filters,
        [groupName]: this.filters[groupName] === optionValue ? "" : optionValue,
      };
      this.applyImmediateFilters();
    },

    selectExclusiveFilter(groupName, optionValue) {
      // Comportement bouton radio : cliquer sur l'option deja active ne fait
      // rien, cliquer sur l'autre bascule dessus. Contrairement a
      // toggleExclusiveFilter, on ne peut jamais se retrouver sans choix.
      if (this.filters[groupName] === optionValue) {
        return;
      }

      this.filters = {
        ...this.filters,
        [groupName]: optionValue,
      };
      this.applyImmediateFilters();
    },
    clearFilterGroup(groupName) {
      this.filters = {
        ...this.filters,
        [groupName]: MULTI_FILTER_KEYS.includes(groupName) ? [] : "",
      };
      this.applyImmediateFilters();
    },
    onSortChanged() {
      this.filters.page = 1;
      this.replaceRouteQuery();
    },
    onViewChanged() {
      this.filters.page = 1;
      this.replaceRouteQuery();
    },
    onPerPageChanged() {
      this.filters.page = 1;
      this.replaceRouteQuery();
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) {
        return;
      }

      this.filters.page = page;
      this.$router.push({
        name: "recipes",
        query: this.buildRouteQueryFromFilters(),
      });
    },
    pushRouteQuery() {
      const nextQuery = this.buildRouteQueryFromFilters();
      const currentQuery = this.buildNormalizedRouteQuery(this.$route.query);

      if (JSON.stringify(nextQuery) === JSON.stringify(currentQuery)) {
        return;
      }

      this.$router.push({
        name: "recipes",
        query: nextQuery,
      });
    },
    replaceRouteQuery() {
      const nextQuery = this.buildRouteQueryFromFilters();
      const currentQuery = this.buildNormalizedRouteQuery(this.$route.query);

      if (JSON.stringify(nextQuery) === JSON.stringify(currentQuery)) {
        return;
      }

      this.$router.replace({
        name: "recipes",
        query: nextQuery,
      }).catch(() => {});
    },
    resetFilters() {
      this.filters = this.getDefaultFilters();
      this.$router.replace({
        name: "recipes",
        query: {},
      }).catch(() => {});
    },
    updateHead() {
      const titleParts = ["Recettes"];
      const activeDishTypeLabels = this.dishTypeOptions
        .filter((option) => this.filters.dish_type.includes(option.value))
        .map((option) => option.label);

      if (activeDishTypeLabels.length) {
        titleParts.unshift(activeDishTypeLabels.join(", "));
      }

      if (this.filters.s) {
        titleParts.unshift(this.filters.s);
      }

      document.title = `${titleParts.join(" | ")} | Mes Meilleurs Menus`;

      const descriptionParts = ["Découvrez les recettes Mes Meilleurs Menus"];

      if (this.filters.s) {
        descriptionParts.push(`autour de "${this.filters.s}"`);
      }

      if (activeDishTypeLabels.length) {
        descriptionParts.push(`filtrées par type de plat : ${activeDishTypeLabels.join(", ").toLowerCase()}`);
      }

      const description =
        `${descriptionParts.join(" ")}. Filtrez-les par profil alimentaire, sans gluten, index glycémique, budget, saison et temps de préparation.`;
      this.setMetaTag("description", description);
      this.setCanonicalLink();
    },
    setMetaTag(name, content) {
      let tag = document.head.querySelector(`meta[name="${name}"]`);

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    },
    setCanonicalLink() {
      let tag = document.head.querySelector('link[rel="canonical"]');

      if (!tag) {
        tag = document.createElement("link");
        tag.setAttribute("rel", "canonical");
        document.head.appendChild(tag);
      }

      tag.setAttribute("href", `${window.location.origin}${this.$route.fullPath}`);
    },
    mediaSrc(uri) {
      if (!uri) return null;
      if (uri.startsWith("http")) return uri;
      return `${process.env.VUE_APP_BASE_URL}${uri}`;
    },
    shortRecipe(recipe) {
      const text = (recipe || "").replace(/\s+/g, " ").trim();

      if (!text) {
        return "Découvrez cette recette Mes Meilleurs Menus et consultez sa préparation détaillée.";
      }

      if (text.length <= 140) {
        return text;
      }

      return `${text.slice(0, 137).trim()}...`;
    },
  },
};
</script>

<style scoped>
.recipes-list-view {
  padding-top: 140px;
  padding-bottom: 48px;
}

.recipes-hero {
  padding: 32px 20px 24px;
}

.recipes-hero__inner {
  max-width: 1320px;
  margin: 0 auto;
}

.recipes-back {
  margin: 0 0 12px;
  padding: 7px 12px;
  border: 1px solid rgba(0, 76, 64, 0.22);
  border-radius: 999px;
  background: #fff;
  color: #004c40;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.recipes-back:hover {
  background: #f5f1e8;
}

.recipes-hero__eyebrow {
  margin: 0 0 8px;
  color: #e57b57;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.recipes-hero__title {
  margin: 0;
  color: #004c40;
  font-family: "Amanise";
  font-size: clamp(36px, 4vw, 60px);
  line-height: 1.05;
}

.recipes-hero__tagline {
  margin: 10px 0 0;
  color: #004c40;
  font-size: clamp(20px, 2vw, 28px);
  font-weight: 800;
  line-height: 1.25;
}

.recipes-hero__subtitle {
  max-width: 780px;
  margin: 16px 0 0;
  color: #35524a;
  font-size: 18px;
  line-height: 1.6;
}

.recipe-finder {
  margin-top: 18px;
  padding: 24px 28px;
  border: 1px solid rgba(0, 76, 64, 0.1);
  border-radius: 26px;
  background: #f5f1e8;
  box-shadow: 0 18px 45px rgba(0, 76, 64, 0.08);
}

.recipe-finder__intro {
  max-width: 760px;
}

.recipe-finder__kicker {
  margin: 0 0 5px;
  color: #e57b57;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.recipe-finder__intro h2 {
  margin: 0;
  color: #004c40;
  font-size: clamp(24px, 2.4vw, 34px);
  line-height: 1.15;
}

.recipe-finder__intro > p:last-child {
  margin: 8px 0 0;
  color: #4a5f58;
  font-size: 15px;
  line-height: 1.55;
}

.recipe-finder__questions {
  display: grid;
  grid-template-columns: 1.15fr 1fr 1fr;
  gap: 18px;
  margin-top: 16px;
}

.recipe-finder__question {
  display: flex;
  gap: 12px;
  min-width: 0;
  padding: 18px;
  border-radius: 18px;
  background: #fff;
}

.recipe-finder__number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #b5d088;
  color: #004c40;
  font-size: 14px;
  font-weight: 900;
}

.recipe-finder__question-body {
  min-width: 0;
  flex: 1;
}

.recipe-finder__question-body label,
.recipe-finder__label {
  display: block;
  margin: 4px 0 12px;
  color: #004c40;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.3;
}

.recipe-finder__question-body input {
  width: 100%;
  min-height: 44px;
  padding: 10px 12px;
  border: 1px solid rgba(0, 76, 64, 0.2);
  border-radius: 12px;
  background: #fff;
  color: #23453c;
  font: inherit;
}

.recipe-finder__question-body input:focus {
  outline: 2px solid rgba(181, 208, 136, 0.7);
  border-color: #6c9a5b;
}

.recipe-finder__question-body small {
  display: block;
  margin-top: 7px;
  color: #6a7a74;
  font-size: 12px;
}

.recipe-finder__choices {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
}

.recipe-finder__choices button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 44px;
  padding: 9px 12px;
  border: 1px solid rgba(0, 76, 64, 0.16);
  border-radius: 13px;
  background: #f8faf7;
  color: #35524a;
  font-size: 13px;
  font-weight: 750;
  line-height: 1.15;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease, color 0.18s ease, transform 0.18s ease;
}

.recipe-finder__choices button:hover {
  transform: translateY(-1px);
  border-color: rgba(0, 76, 64, 0.45);
}

.recipe-finder__choices button.is-active {
  border-color: #004c40;
  background: #004c40;
  color: #fff;
  box-shadow: 0 5px 12px rgba(0, 76, 64, 0.13);
}

.recipe-finder__question--time .recipe-finder__choices {
  grid-template-columns: 1fr;
}

.recipe-finder__question--time .recipe-finder__choices button {
  justify-content: flex-start;
  padding-left: 15px;
}

.recipe-finder__question--time .recipe-finder__choices button::before {
  content: "⏱";
  font-size: 15px;
}

.recipe-finder__question--time .recipe-finder__choices button:first-child::before {
  content: "✓";
}

.recipe-finder__footer {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 22px;
}

.recipe-finder__submit,
.recipe-finder__all {
  min-height: 46px;
  padding: 11px 18px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
}

.recipe-finder__submit {
  border: 1px solid #004c40;
  background: #004c40;
  color: #fff;
}

.recipe-finder__submit:hover {
  background: #063d35;
}

.recipe-finder__all {
  border: 1px solid rgba(0, 76, 64, 0.2);
  background: transparent;
  color: #004c40;
}

.recipes-section {
  padding: 0 20px;
}

.recipes-section__inner {
  max-width: 1320px;
  margin: 0 auto;
}

.recipes-layout {
  display: grid;
  grid-template-columns: minmax(280px, 320px) minmax(0, 1fr);
  gap: 32px;
  align-items: start;
}

.recipes-sidebar,
.recipes-content {
  min-width: 0;
}

.recipes-filters {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px;
  border-radius: 18px;
  background: #f5f1e8;
  position: sticky;
  top: 126px;
}

.recipes-filters__stack {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.recipes-filter-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 18px;
  border-top: 1px solid rgba(0, 76, 64, 0.12);
}

.recipes-filter-group:first-of-type {
  padding-top: 0;
  border-top: none;
}

.recipes-filter-group__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.recipes-filter-group__title {
  margin: 0;
  color: #004c40;
  font-size: 15px;
  font-weight: 700;
}

.recipes-filter-group__clear {
  border: none;
  background: none;
  color: #6a7a74;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
}

.recipes-filter-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.recipes-filter-option {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 46px;
  padding: 10px 12px;
  border: 1px solid #d8ddd2;
  border-radius: 12px;
  background: #fff;
  color: #35524a;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}

.recipes-filter-option input {
  width: 16px;
  height: 16px;
  accent-color: #004c40;
  flex: 0 0 auto;
}

.recipes-filter-option span {
  font-size: 14px;
  font-weight: 500;
}

.recipes-filter-option.is-active {
  border-color: #004c40;
  background: rgba(0, 76, 64, 0.06);
  box-shadow: inset 0 0 0 1px rgba(0, 76, 64, 0.08);
}

.recipes-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.recipes-field--compact {
  min-width: 160px;
}

.recipes-field__label {
  color: #004c40;
  font-size: 14px;
  font-weight: 700;
}

.recipe-finder__autocomplete {
  position: relative;
}

.recipe-finder__autocomplete-menu {
  text-align: left;
}

.recipe-finder__autocomplete-menu .recipes-autocomplete__option small {
  display: inline;
  margin: 0;
}

.recipes-autocomplete {
  position: relative;
}

.recipes-autocomplete__menu {
  position: absolute;
  z-index: 20;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  overflow: hidden;
  border: 1px solid rgba(0, 76, 64, 0.18);
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 12px 28px rgba(0, 76, 64, 0.14);
}

.recipes-autocomplete__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-bottom: 1px solid rgba(0, 76, 64, 0.08);
  background: #fff;
  color: #23453c;
  text-align: left;
  cursor: pointer;
}

.recipes-autocomplete__option:last-child {
  border-bottom: 0;
}

.recipes-autocomplete__option:hover,
.recipes-autocomplete__option.is-active {
  background: #f2f7ed;
}

.recipes-autocomplete__option > span {
  font-size: 13px;
  font-weight: 800;
}

.recipes-autocomplete__option > small {
  flex: 0 0 auto;
  margin: 0;
  color: #e57b57;
  font-size: 10px;
  font-weight: 800;
}

.recipes-field--free-search small {
  display: block;
  margin-top: 6px;
  color: #6a7a74;
  font-size: 11px;
  line-height: 1.35;
}

.recipes-free-search__submit {
  width: 100%;
  margin-top: 10px;
  min-height: 40px;
  border: 1px solid #004c40;
  border-radius: 11px;
  background: #004c40;
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}

.recipes-free-search__submit:hover {
  background: #063d35;
}

.recipe-card__season-note {
  margin: 0 0 10px;
  padding: 8px 10px;
  border-radius: 10px;
  background: #f5f1e8;
  color: #6a5b32;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
}

.recipes-field__control {
  min-height: 46px;
  padding: 10px 12px;
  border: 1px solid #d8ddd2;
  border-radius: 12px;
  background: #fff;
  color: #35524a;
}

.recipes-filters__actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 4px;
}

.recipes-filters__actions--top {
  margin-top: 0;
}

.recipes-submit,
.recipes-reset {
  width: 100%;
  min-height: 46px;
  padding: 0 18px;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
}

.recipes-submit {
  border: 1px solid #004c40;
  background: #004c40;
  color: #fff;
}

.recipes-reset {
  border: 1px solid #004c40;
  background: transparent;
  color: #004c40;
}

.recipes-reset:disabled {
  opacity: 0.45;
  cursor: default;
}

.recipes-results-heading {
  margin: 0 0 18px;
}

.recipes-results-heading__eyebrow {
  margin: 0 0 4px;
  color: #e57b57;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.recipes-results-heading h2 {
  margin: 0;
  color: #004c40;
  font-size: clamp(25px, 2.4vw, 34px);
  line-height: 1.15;
}

.recipes-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: end;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 24px;
}

.recipes-toolbar__summary {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.recipes-toolbar__count,
.recipes-toolbar__page {
  margin: 0;
  color: #35524a;
}

.recipes-toolbar__count {
  color: #004c40;
  font-size: 18px;
  font-weight: 700;
}

.recipes-toolbar__controls {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: end;
}

.recipes-season-notice {
  margin: 0 0 22px;
  padding: 24px;
  border-radius: 18px;
  background: #f5f1e8;
  color: #35524a;
  text-align: center;
}

.recipes-season-notice h2 {
  max-width: 720px;
  margin: 0 auto 10px;
  color: #004c40;
  font-size: clamp(20px, 2vw, 25px);
  line-height: 1.25;
}

.recipes-season-notice p {
  max-width: 760px;
  margin: 0 auto;
  line-height: 1.6;
}

.recipes-empty {
  padding: 48px 24px;
  border-radius: 18px;
  background: #f5f1e8;
  color: #35524a;
  text-align: center;
}

.recipes-empty h2 {
  max-width: 720px;
  margin: 0 auto 10px;
  color: #004c40;
  font-size: clamp(21px, 2vw, 28px);
  line-height: 1.25;
}

.recipes-empty p {
  max-width: 720px;
  margin: 0 auto;
  line-height: 1.6;
}

.recipes-empty__eyebrow {
  margin-bottom: 8px !important;
  color: #e57b57;
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.recipes-empty__action {
  margin-top: 22px;
  min-height: 44px;
  padding: 10px 18px;
  border: 1px solid #004c40;
  border-radius: 999px;
  background: #004c40;
  color: #fff;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
}

.recipes-empty__action:hover {
  background: #063d35;
}

.recipes-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
}

.recipes-grid--list {
  grid-template-columns: 1fr;
}

.recipes-grid--list .recipe-card__link {
  flex-direction: row;
}

.recipes-grid--list .recipe-card__media {
  flex: 0 0 220px;
  aspect-ratio: auto;
  height: 180px;
  min-height: 180px;
  overflow: hidden;
}

.recipes-grid--list .recipe-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.recipes-grid--list .recipe-card__title {
  font-size: 17px;
}

.recipes-grid--list .recipe-card__mealim-score {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  width: fit-content;
  margin: 2px 0 8px;
  padding: 5px 10px;
  border-radius: 999px;
  background: #edf4ee;
  color: #315f46;
  font-size: 11px;
  line-height: 1.2;
}
.recipe-card__mealim-score strong {
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
.recipe-card__badges {
  display: flex;
}

.recipes-grid--list .recipe-card__quickfacts {
  display: none;
}

.recipe-card__mealim-score {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  width: fit-content;
  margin: 2px 0 8px;
  padding: 5px 10px;
  border-radius: 999px;
  background: #edf4ee;
  color: #315f46;
  font-size: 11px;
  line-height: 1.2;
}
.recipe-card__mealim-score strong {
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
.recipe-card__badges {
  display: none;
  flex-wrap: wrap;
  gap: 6px;
  margin: 10px 0;
}

.recipe-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  background: #f0f4f0;
  color: #35524a;
  font-size: 12px;
  font-weight: 600;
}

.recipe-badge--yes {
  background: #e6f4ec;
  color: #1e7d3f;
}

.recipe-badge--no {
  background: #f4f4f4;
  color: #555;
}

.recipe-card {
  min-width: 0;
}

.recipe-card__link {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 10px 30px rgba(0, 76, 64, 0.08);
  color: inherit;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.recipe-card__link:hover {
  transform: translateY(-3px);
  box-shadow: 0 16px 36px rgba(0, 76, 64, 0.14);
}

.recipe-card__media {
  position: relative;
  aspect-ratio: 4 / 3;
  background: #e8efe8;
}

.recipe-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  height: 180px;
}

.recipe-card__image--fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: linear-gradient(135deg, rgba(181, 208, 136, 0.9), rgba(0, 76, 64, 0.9));
  color: #fff;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  text-align: center;
}

.recipe-card__content {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 17px;
}

.recipe-card__meta {
  margin: 0 0 7px;
  color: #e57b57;
  font-size: 13px;
  font-weight: 800;
}

.recipe-card__quickfacts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0 0 10px;
}

.recipe-card__quickfacts span {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 4px 8px;
  border-radius: 999px;
  background: #f5f1e8;
  color: #35524a;
  font-size: 11px;
  font-weight: 800;
  line-height: 1;
}

.recipe-card__quickfacts span:first-child {
  color: #c96443;
}

.recipe-card__title {
  margin: 0 0 10px;
  color: #004c40;
  font-size: 18px;
  line-height: 1.25;
}

.recipe-card__excerpt {
  margin: 0;
  color: #4a5f58;
  font-size: 15px;
  line-height: 1.6;
}

.recipe-card__cta {
  margin-top: auto;
  padding-top: 12px;
  color: #004c40;
  font-size: 13px;
  font-weight: 800;
}

/* ── Bouton filtres fixe (mobile) ── */
.recipes-filter-fab {
  display: none;
}

.recipes-filter-overlay {
  display: none;
}

@media only screen and (max-width: 1023px) {
  .recipe-finder__questions {
    grid-template-columns: 1fr;
  }

  .recipe-finder__footer {
    flex-wrap: wrap;
  }

  .recipe-finder__submit,
  .recipe-finder__all {
    flex: 1 1 220px;
  }

  .recipes-filter-fab {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    bottom: 24px;
    right: 20px;
    z-index: 200;
    padding: 12px 22px;
    border: none;
    border-radius: 50px;
    background: #004c40;
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    box-shadow: 0 4px 16px rgba(0, 76, 64, 0.35);
    cursor: pointer;
  }

  .recipes-filter-overlay {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 149;
    background: rgba(0, 0, 0, 0.4);
  }

  .recipes-sidebar {
    display: none;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 150;
    max-height: 80vh;
    overflow-y: auto;
    border-radius: 20px 20px 0 0;
    box-shadow: 0 -4px 24px rgba(0, 76, 64, 0.2);
  }

  .recipes-sidebar--open {
    display: block;
  }
}

.recipes-pagination {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 32px;
}

.recipes-pagination__button {
  min-width: 44px;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid #d8ddd2;
  border-radius: 12px;
  background: #fff;
  color: #35524a;
  cursor: pointer;
}

.recipes-pagination__button--active {
  border-color: #004c40;
  background: #004c40;
  color: #fff;
}

.recipes-pagination__button:disabled {
  opacity: 0.45;
  cursor: default;
}

@media only screen and (max-width: 1199px) {
  .recipes-grid:not(.recipes-grid--list) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media only screen and (max-width: 1023px) {
  .recipes-layout {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .recipes-filters {
    position: static;
  }

  .recipes-filter-options {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .recipes-grid:not(.recipes-grid--list) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media only screen and (max-width: 899px) {
  .recipes-list-view {
    padding-top: 110px;
  }

  .recipes-grid:not(.recipes-grid--list) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .recipes-grid--list {
    grid-template-columns: 1fr;
  }

  .recipes-grid--list .recipe-card__link {
    flex-direction: column;
  }

  .recipes-grid--list .recipe-card__media {
    flex: none;
    height: 180px;
    min-height: 0;
    aspect-ratio: auto;
  }
}

@media only screen and (max-width: 599px) {
  .recipes-hero {
    padding-left: 16px;
    padding-right: 16px;
  }

  .recipes-section {
    padding-left: 16px;
    padding-right: 16px;
  }

  .recipes-filters {
    padding: 18px;
  }

  .recipes-filter-options {
    grid-template-columns: 1fr;
  }

  .recipes-toolbar {
    align-items: stretch;
  }

  .recipes-toolbar__controls {
    flex-direction: column;
    align-items: stretch;
  }

  .recipes-field--compact {
    min-width: 0;
  }

  .recipes-grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  .recipe-card__content {
    padding: 18px;
  }

  .recipe-card__title {
    font-size: 20px;
  }
}
</style>